/**
 * Generated theme suggestions stored in the browser per user + project + recording
 * scope. Best-effort: storage may be unavailable or full, so every access is guarded.
 */
import type { ThemeCandidate } from "../../../../store/state/qce/agentService";

const PREFIX = "ts_theme_suggestions_v1";

export interface StoredSuggestions {
  candidates: ThemeCandidate[];
  /** Names the user dismissed (kept so "Generate more" skips them). */
  dismissed: string[];
}

const EMPTY: StoredSuggestions = { candidates: [], dismissed: [] };

// Case/punctuation-insensitive, matching KS's skipNames rule.
export const normalizeThemeName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[\s.,;:!?'"()[\]{}&/\\_-]+/g, " ")
    .trim();

export function suggestionsStorageKey(userId: string, projectId: string, storyIds?: string[]): string {
  const scope = storyIds?.length ? [...storyIds].sort().join(",") : "all";
  return `${PREFIX}:${userId}:${projectId}:${scope}`;
}

export function loadSuggestions(key: string): StoredSuggestions {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<StoredSuggestions>;
    return {
      candidates: Array.isArray(parsed.candidates) ? parsed.candidates : [],
      dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed : [],
    };
  } catch {
    return EMPTY;
  }
}

export function saveSuggestions(key: string, value: StoredSuggestions): void {
  try {
    if (value.candidates.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable or full — suggestions just won't survive a close */
  }
}

export function clearSuggestions(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

/** Append new candidates (dedupe by name) and persist; returns how many were new. */
export function appendSuggestions(key: string, found: ThemeCandidate[]): number {
  const current = loadSuggestions(key);
  const seen = new Set(current.candidates.map((c) => normalizeThemeName(c.name)));
  const fresh = found.filter((c) => {
    const n = normalizeThemeName(c.name);
    if (!n || seen.has(n)) return false;
    seen.add(n);
    return true;
  });
  if (fresh.length) saveSuggestions(key, { ...current, candidates: [...current.candidates, ...fresh] });
  return fresh.length;
}
